import { useEffect, useRef } from "react";
import { useMyWorkItems } from "../workitems";
import { isBadgeRefresh } from "../push/swMessages";
import { publishAppBadgeObservation } from "./appBadge";

/**
 * Safety re-read cadence while the page is visible. It bounds how long a missed
 * realtime event or push can leave the taskbar number wrong while the app is
 * open. React Query pauses it while the document is hidden
 * (refetchIntervalInBackground stays false), so a background window costs
 * nothing; the worker's push path covers the closed and hidden cases.
 */
export const BADGE_RECHECK_INTERVAL_MS = 60_000;

/**
 * Single mount point for the taskbar App Badge.
 *
 * Reads the deduplicated count of the current user's outstanding (open +
 * pending, not-completed) work items and publishes each fresh server read to
 * the service worker, which is the single writer of the badge (see
 * app-badge/badgeWriter.ts). Renders nothing.
 *
 * What counts as an observation: a fresh server response, identified by its
 * `asOf` instant. A realtime cache patch changes `totalCount` but keeps the
 * `asOf` of the read it was derived from, so it is not published. The badge
 * only ever shows what the server said at a stated time.
 *
 * Re-reads that move the number (all of which the worker fences by `asOf`):
 *   - realtime invalidations and the route's own fetches (`["workitems","mine"]`);
 *   - the periodic safety re-read while visible;
 *   - returning to the foreground (`visibilitychange`, `pageshow`, `focus`,
 *     `online`), because a resumed document does not always reach React
 *     Query's focus manager;
 *   - the worker's `BADGE_REFRESH` hint after a push arrives.
 *
 * Mount once, as a sibling of the realtime bridges inside the authenticated
 * shell. `take: 1` keeps the response to one row; only `totalCount` and `asOf`
 * are consumed.
 *
 * There is deliberately no clear-on-unmount. The badge persists after the app
 * closes ("you still owe N items"), and the next observation corrects it.
 * Nothing is published before the first read resolves, so a persisted badge is
 * not cleared on a cold start.
 */
export function AppBadgeBridge(): null {
    const { data, refetch } = useMyWorkItems({
        includeCompleted: false,
        skip: 0,
        take: 1,
        refetchInterval: BADGE_RECHECK_INTERVAL_MS,
    });

    // Last server read published. Compared by `asOf`, not by object identity:
    // a cache patch produces a new `data` object carrying the same `asOf`, and
    // must not be published as if the server had said so.
    const publishedAsOf = useRef<string | undefined>(undefined);
    useEffect(() => {
        const asOf = data?.asOf;
        if (data === undefined || asOf === undefined || asOf === null) {
            return;
        }
        if (asOf === publishedAsOf.current) {
            return;
        }
        publishedAsOf.current = asOf;
        void publishAppBadgeObservation({ count: data.totalCount, asOf });
    }, [data]);

    // Re-read on every foreground transition and when the worker hints. The
    // refetch does not cancel an in-flight read, so the several events a single
    // return to the foreground fires share one request.
    useEffect(() => {
        const reread = (): void => {
            void refetch({ cancelRefetch: false });
        };
        const onVisibility = (): void => {
            if (document.visibilityState === "visible") {
                reread();
            }
        };
        const onWorkerMessage = (event: MessageEvent): void => {
            if (isBadgeRefresh(event.data)) {
                reread();
            }
        };

        document.addEventListener("visibilitychange", onVisibility);
        window.addEventListener("pageshow", reread);
        window.addEventListener("focus", onVisibility);
        window.addEventListener("online", reread);
        const serviceWorker = "serviceWorker" in navigator ? navigator.serviceWorker : undefined;
        serviceWorker?.addEventListener("message", onWorkerMessage);
        return () => {
            document.removeEventListener("visibilitychange", onVisibility);
            window.removeEventListener("pageshow", reread);
            window.removeEventListener("focus", onVisibility);
            window.removeEventListener("online", reread);
            serviceWorker?.removeEventListener("message", onWorkerMessage);
        };
    }, [refetch]);

    return null;
}
